import i18n from 'helper/i18n';
import { ProgramDaysStateType } from 'store/programDays';
import * as programItemsActionTypes from 'store/programDays';
import { DateType } from 'types/Date';
import { ContentTypeProductDtoType } from 'types/services/magnolia/dtos/ContentTypeProductDto';
import { ContentTypeRecipeDtoType } from 'types/services/magnolia/dtos/ContentTypeRecipeDto';
import { ContentTypeTipDtoType } from 'types/services/magnolia/dtos/ContentTypeTipDto';
import { ContentTypeTodoDtoType } from 'types/services/magnolia/dtos/ContentTypeTodoDto';
import { ContentTypeWorkoutDtoType } from 'types/services/magnolia/dtos/ContentTypeWorkoutDto';
import { ImageDtoType } from 'types/services/magnolia/dtos/ImageDto';
import { ProgramItemDtoType } from 'types/services/spring/dtos/ProgramItemDto';
// import type { ProgramItemDtoType as MagnoliaProgramItemDtoType } from 'types/services/magnolia/dtos/ProgramItemDto';
// import type { ProgramItemDtoType as SpringProgramItemDtoType } from 'types/services/spring/dtos/ProgramItemDto';

export const UNIT_MAP: { [key: string]: string } = {
	repetition: i18n('react.impulsCoach.components.cardDetail.workout.unit.repetition'),
	distance: i18n('react.impulsCoach.components.cardDetail.workout.unit.distance'),
	duration: i18n('react.impulsCoach.components.cardDetail.workout.unit.duration'),
	durationSec: i18n('react.impulsCoach.components.cardDetail.workout.unit.durationSec'),
};

export type ImageType = ImageDtoType;
export type ImageMinType = ImageDtoType;

export type NormalizedImageType = {
	image: {
		srcset: ImageType['srcset'];
		alt: ImageType['alt'];
		title: ImageType['title'];
	};
	caption: {
		title: ImageType['title'];
		text: ImageType['text'];
	};
	lightbox: {
		image: {
			srcset: ImageType['srcset'];
			alt: ImageType['alt'];
			title: ImageType['title'];
		};
	};
};

export type ImagesType = Array<ImageType>;
export type NormalizedImagesType = Array<NormalizedImageType>;

export type IconMapType = {
	slogan: string;
	recommendation: string;
	dynamicProduct: string;
};

export type IconMapTypeType = 'slogan' | 'recommendation' | 'dynamicProduct' | string;

export type ExerciseUnit = keyof typeof UNIT_MAP;

export type VideoType = {
	embedCode: string;
	title: string;
	text: string;
};

export type NormalizedVideoType = {
	video: {
		code: VideoType['embedCode'];
	};
	caption?: {
		title?: VideoType['title'];
		text?: VideoType['text'];
	};
};

export type LinkType = {
	title: string;
	href: string;
	openInNewWindow: boolean;
};

export type ButtonLinkType = {
	modifier: 'button';
} & LinkType;

export type ExerciseType = {
	images: ImagesType;
	video: VideoType;
	title: string;
	previewImage: ImageType;
	bodyRegions: Array<string>;
	unit: ExerciseUnit;
	durationPerUnit: number;
	met: number;
	contentType: 'EXERCISE';
	externalId: string;
};

export type WorkoutSet = {
	pausePerExercise: number;
	repetitions: number;
	amount: number;
	exercise: ExerciseType;
};

export type NormalizedExerciseType = {
	title: string;
	amount: WorkoutSet['amount'];
	bodyRegions: ExerciseType['bodyRegions'];
	images: null | NormalizedImagesType;
	contentVideo: null | NormalizedVideoType;
	unit: string;
};
export type NormalizedWorkoutSet = {
	id: number;
	href: string;
	cells: [
		{
			image: ImageType | null;
		},
		{
			text: string;
		},
		{
			text: string;
		}
	];
};

export type SelectedContent = ContentTypeRecipeDtoType &
	ContentTypeProductDtoType &
	ContentTypeTodoDtoType &
	ContentTypeTipDtoType &
	ContentTypeWorkoutDtoType;

export type ProgramItemContent = {
	productId: string;
	disableRetailerContext?: boolean;
	validFrom: DateType;
	validTo: DateType;
	contentTypes: Array<Object>;
	externalId: string;
	fallbackImage: ImageMinType;
	itemType: string;
	slogans: Array<string>;
	selectedContent: SelectedContent;
	menuTypeLabel?: string;
	menuTypeFilterTagId?: string;
};

export type ProgramItemType = ProgramItemDtoType & {
	isCustom: boolean;
	isPostponed: boolean;
};

export type ProgramItemsType = Array<ProgramItemType>;
export type ProgramItemsObjectType = {
	[key: string]: ProgramItemType;
};

export type ProgramItemStateType = {
	readonly byId: {
		[key: string]: ProgramItemType;
	};
	readonly contentByExternalId: {
		[key: string]: ProgramItemContent;
	};
	readonly contentAllExternalIds: string[];
	readonly isUpdatingTracking: boolean;
};

export type NormalizedRecipe = {
	baseSize: SelectedContent['baseSize'];
	duration: SelectedContent['duration'];
	totalDuration: SelectedContent['totalDuration'];
	totalDurationFormatted: SelectedContent['totalDurationFormatted'];
	nutrition: SelectedContent['nutrition'];
	steps: SelectedContent['steps'];
	hints: SelectedContent['hints'];
	nutrients: SelectedContent['nutrients'];
	ingredients: SelectedContent['ingredients'];
	contains: SelectedContent['contains'];
	variants: SelectedContent['variants'];
};

export type RootStateType = {
	programItems: ProgramItemStateType;
	programDays: ProgramDaysStateType;
};

export const actionTypes = programItemsActionTypes;

export const initialState: ProgramItemStateType = {
	byId: {},
	contentByExternalId: {},
	contentAllExternalIds: [],
	isUpdatingTracking: false,
};

export type ProgramItem = {
	id: string;
	externalId: string;
	done: boolean;
	selectedContentType: string;
	type: string;
};

export const mapToFrontendType = (type: string): string | null => {
	switch (type) {
		case 'RECIPE':
			return 'recipe';
		case 'SLOGAN':
			return 'slogan';
		case 'TRACKING':
			return 'tracking';
		case 'TIP':
			return 'tip';
		case 'WORKOUT':
			return 'workout';
		case 'TODO':
			return 'exercise';
		case 'PRODUCT':
			return 'recommendation';
		case 'PRODUCT_DYNAMIC':
			return 'dynamicProduct';
		default:
			return null;
	}
};

export const mapToBackendType = (type: string): string | null => {
	switch (type) {
		case 'recipe':
			return 'RECIPE';
		case 'slogan':
			return 'SLOGAN';
		case 'tracking':
			return 'TRACKING';
		case 'tip':
			return 'TIP';
		case 'workout':
			return 'WORKOUT';
		case 'task':
			return 'TODO';
		case 'recommendation':
			return 'PRODUCT';
		case 'dynamicProduct':
			return 'PRODUCT_DYNAMIC';
		default:
			return null;
	}
};
